import { useState } from "react";
import { useAsync } from "../../../shared/hooks/useAsync";
import { useFormFields } from "../../../shared/hooks/useFormFields";
import { authenticationApi } from "../api/authentication.api";
import { validators, isValid, validateFields } from "../validation/authentication.validation";
import { useSession } from "./useSession";

export const SIGNUP_STEP = { DETAILS: "details", OTP: "otp" };

/**
 * Two-step signup: collect details, send an OTP to the email (the
 * account is NOT created yet), then verify it to create the account and
 * establish the session — proving email ownership before account creation.
 */
export function useSignup({ onSuccess }) {
    const { fields, set } = useFormFields({ name: "", email: "", password: "" });
    const [fieldErrors, setFieldErrors] = useState({});
    const [step, setStep] = useState(SIGNUP_STEP.DETAILS);
    const [otp, setOtp] = useState("");
    const [otpError, setOtpError] = useState(null);
    const { run, loading, error, clearError } = useAsync();
    const { establishSession } = useSession();

    const handleDetailsSubmit = async (e) => {
        e.preventDefault();

        const errors = validateFields(fields);
        setFieldErrors(errors);
        if (!isValid(errors)) return;

        const data = await run(() =>
            authenticationApi.signup(fields.name, fields.email, fields.password)
        );
        if (!data) return;

        setStep(SIGNUP_STEP.OTP);
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();

        const otpErr = validators.otp(otp);
        setOtpError(otpErr);
        if (otpErr) return;

        const data = await run(() =>
            authenticationApi.verifySignup(fields.name, fields.email, fields.password, otp)
        );
        if (!data) return;

        if (data.token) establishSession(data.token);
        onSuccess?.();
    };

    const resendOtp = async () => {
        setOtp("");
        setOtpError(null);
        await run(() =>
            authenticationApi.signup(fields.name, fields.email, fields.password)
        );
    };

    const backToDetails = () => {
        clearError();
        setOtp("");
        setOtpError(null);
        setStep(SIGNUP_STEP.DETAILS);
    };

    return {
        fields, set, fieldErrors,
        step, otp, setOtp, otpError,
        loading, error,
        handleDetailsSubmit, handleOtpSubmit, resendOtp, backToDetails,
    };
}
